import React, { useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Checkbox,
} from "@mui/material";
import { RemoveCircleOutline } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import { LangConstant } from "const";

const UserTable = ({ users, selectedUsers = [], onActionClick, actionType, loadMore, hasMore, onSelected }) => {
  const observer = useRef(null);
  const classes = useStyles();
  const { t: getLabel } = useTranslation();
  const lastUserRef = useCallback(
    node => {
      if (!node || !hasMore) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            loadMore();
          }
        },
        { threshold: 1.0 },
      );

      observer.current.observe(node);
    },
    [hasMore, loadMore],
  );
  return (
    <Box sx={{ flex: 1 }}>
      <TableContainer sx={{ maxHeight: 550, overflowY: "auto" }}>
        <Table stickyHeader sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.titleCell} sx={{ width: "5%" }} align="center">
                {actionType === "add" && getLabel("TXT_STT")}
              </TableCell>
              <TableCell className={classes.titleCell} sx={{ width: "25%" }}>
                {getLabel("TXT_FULL_NAME")}
              </TableCell>
              <TableCell className={classes.titleCell} align="left" width="13%">
                {getLabel("TXT_PHONENUMBER")}
              </TableCell>
              <TableCell className={classes.titleCell} align="left" width="13%">
                {getLabel("TXT_UNIT")}
              </TableCell>
              <TableCell className={classes.titleCell} align="left" width="13%">
                {getLabel("TXT_DEPARTMENT")}
              </TableCell>
              <TableCell className={classes.titleCell} align="left" width="13%">
                {getLabel("TXT_POSITION")}
              </TableCell>
              <TableCell className={classes.titleCell} sx={{ width: "5%" }} align="center"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length > 0 ? (
              users.map((user, index) => (
                <TableRow key={user.id} ref={index === users.length - 1 ? lastUserRef : null}>
                  <TableCell align="center">
                    {actionType === "remove" ? (
                      <Checkbox
                        size="small"
                        checked={selectedUsers.some(selected => selected.id === user.id)}
                        onChange={() => onSelected(user)}
                        sx={{ width: 24, height: 24 }}
                      />
                    ) : (
                      index + 1
                    )}
                  </TableCell>
                  <TableCell>{user.name}</TableCell>
                  <TableCell align="left">{user.phone || getLabel(LangConstant.TXT_NOT_FOUND)}</TableCell>
                  <TableCell align="left">{user.unitName || getLabel(LangConstant.TXT_NOT_FOUND)}</TableCell>
                  <TableCell align="left">{user.departmentName || getLabel(LangConstant.TXT_NOT_FOUND)}</TableCell>
                  <TableCell align="left">{user.positionName || getLabel(LangConstant.TXT_NOT_FOUND)}</TableCell>
                  <TableCell align="center">
                    {actionType === "add" ? (
                      <Checkbox
                        size="small"
                        sx={{ width: 24, height: 24 }}
                        onChange={() => onActionClick(user)}
                        checked={selectedUsers.some(selected => selected.id === user.id)}
                      />
                    ) : (
                      <IconButton color="error" onClick={() => onActionClick(user)} sx={{ width: 24, height: 24 }}>
                        <RemoveCircleOutline fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <i>{getLabel("TXT_DATA_NOT_FOUND")}</i>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default UserTable;

const useStyles = makeStyles(() => ({
  titleCell: {
    fontWeight: "bold",
    backgroundColor: "#F1F1F1",
  },
}));
